import React from 'react'
import { useSelector } from 'react-redux'
import { Box, Button, Spacer, Text, Container, Icon, hooks } from 'roadie-ui'
import { FeatureFlag } from 'Optimizely'
import { createOnboarding } from 'util/server/mainApi'
import LegacyIcon from 'components/misc/Icon'
import { ExternalLink } from 'components/layout/links'
import { sendNativeMessage } from 'util/native'
import styled from 'styled-components'
import { DrivingProfile } from 'types/profile'
import { getLicenseExpirationDate } from 'util/vehicle'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

type LicenseInfoCardProps = {
  children: (closeCard: () => void) => React.ReactNode | undefined
  defaultEdit?: boolean
  icon: React.ReactElement
  title: string
  info: React.ReactNode
  formIsEmpty?: boolean
}

export const LicenseInfoCard: React.FC<LicenseInfoCardProps> = (props) => {
  const [editing, setEditing] = React.useState(props.defaultEdit ?? false)
  const isLocked = useSelector<any, boolean>((state) => state.isLocked)

  const toggleEditing = React.useCallback(() => {
    setEditing(!editing)
  }, [editing])
  const cardPadding = {
    mobile: '0 2.4rem 2rem',
    tablet: '0 2.4rem 2rem 10.4rem',
    desktop: '0 2.4rem 2rem 10.4rem',
    ultrawide: '0 2.4rem 2rem 10.4rem',
  }

  const actionText = editing ? 'Cancel' : props.formIsEmpty ? 'Add' : 'Edit'
  const actionContent = hooks.useBreakPoints<React.ReactNode>({
    mobile: editing ? (
      <LegacyIcon symbol="cross" width="2.4rem" height="2.4rem" />
    ) : props.formIsEmpty ? (
      <LegacyIcon symbol="plus" width="2.4rem" height="2.4rem" />
    ) : (
      <LegacyIcon symbol="edit" width="2.4rem" height="2.4rem" />
    ),
    tablet: <Text.Button $color={isLocked ? 'neutral-base' : 'primary-base'}>{actionText}</Text.Button>,
    desktop: <Text.Button $color={isLocked ? 'neutral-base' : 'primary-base'}>{actionText}</Text.Button>,
    ultrawide: <Text.Button $color={isLocked ? 'neutral-base' : 'primary-base'}>{actionText}</Text.Button>,
  })

  const cardContent = (
    <Container.Grid $padding="2.4rem" $gridTemplateColumns="5rem 1fr max-content" $alignItems="center">
      <Container.Position $position="relative" $width="5rem" $height="5rem">
        <Container.Position $position="absolute" $top="0" $bottom="0" $left="0" $right="0">
          <Container.Base $borderRadius="100%" $backgroundColor="primary-lighter" $height="100%" />
        </Container.Position>
        <Container.Position $color="primary-dark" $position="absolute" $top="0" $left="0" $right="0" $padding=".75rem">
          {props.icon}
        </Container.Position>
      </Container.Position>
      <Container.Base $textAlign="left" $padding="0 1rem 0 3rem">
        <Text.H5 $fontWeight="400" $color="neutral-black">
          {props.title}
        </Text.H5>
        <Text.Caption>{props.info}</Text.Caption>
      </Container.Base>
      {editing || isLocked ? (
        <Button.Tertiary type="button" onClick={toggleEditing} disabled={isLocked}>
          <Container.Grid $justifyItems="end">{actionContent}</Container.Grid>
        </Button.Tertiary>
      ) : (
        <Container.Grid $justifyItems="end">{actionContent}</Container.Grid>
      )}
    </Container.Grid>
  )

  return (
    <>
      {!editing && !isLocked ? (
        <Button.Tertiary
          type="button"
          onClick={toggleEditing}
          $display="block"
          $width="100%"
          $textAlign="inherit"
          $textTransform="none"
          $letterSpacing="0"
        >
          {cardContent}
        </Button.Tertiary>
      ) : (
        cardContent
      )}
      {editing ? (
        <Box.Padding $paddingMedia={cardPadding}>{props.children(() => setEditing(false))}</Box.Padding>
      ) : (
        <></>
      )}
    </>
  )
}

const IdvSteps = styled.ol`
  counter-reset: idv;
  list-style: none;
  display: grid;
  grid-gap: 1.2rem;
  margin: 0;
  padding: 0;
  max-width: 25rem;
  justify-self: center;
`

const IdvStep = styled.li`
  counter-increment: idv;
  position: relative;
  padding-left: 3rem;

  :before {
    content: counter(idv);
    position: absolute;
    top: 0;
    left: 0;
    width: 1.8rem;
    height: 1.8rem;
    padding-top: 0.25rem;
    text-align: center;
    font-size: 1.3rem;
    border-radius: 100%;
    color: inherit;
    background-color: ${(props) => props.theme.colors.primaryDark};
  }
`
const LicenseIDVForm: React.FC = () => {
  const handleIdv = React.useCallback(() => {
    sendNativeMessage({ type: 'OPEN_IDV' })
    createOnboarding()
  }, [])

  return (
    <Container.Grid $color="neutral-black">
      <Text.Body2>
        <LocalizedText translationKey="dweb_license_info_update_steps_intro" />
      </Text.Body2>
      <Spacer $height="1.6rem" />
      <IdvSteps>
        <IdvStep>
          <Text.Body2>
            <LocalizedText translationKey="dweb_account_driving_info_license_info_capture_front_license" />
          </Text.Body2>
        </IdvStep>
        <IdvStep>
          <Text.Body2>
            <LocalizedText translationKey="dweb_account_driving_info_license_info_capture_back_license" />
          </Text.Body2>
        </IdvStep>
        <IdvStep>
          <Text.Body2>
            <LocalizedText translationKey="dweb_account_driving_info_license_info_take_selfie" />
          </Text.Body2>
        </IdvStep>
      </IdvSteps>
      <Spacer $height="2.4rem" />
      <Button.Primary onClick={handleIdv} $width="100%">
        <LocalizedText translationKey="dweb_account_driving_info_license_info_update_license" />
      </Button.Primary>
      <Spacer $height="2.4rem" />
      <Text.Caption>
        <LocalizedText translationKey="dweb_account_driving_info_license_info_by_tapping_update_license_above_agree_we_our" />
      </Text.Caption>
    </Container.Grid>
  )
}

const LicenseNotification: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props

  return (
    <Box.Simple $backgroundColor="neutral-lightest" $padding="1rem" $borderRadius="4px">
      <Text.Body2 $color="neutral-black">{children}</Text.Body2>
    </Box.Simple>
  )
}

const LicenseInfoBody: React.FC = () => {
  const isWebview = useSelector<any, boolean>((state) => state.config.isWebview)

  if (isWebview) {
    return <LicenseIDVForm />
  } else {
    return (
      <LicenseNotification>
        <LocalizedText translationKey="dweb_license_info_update_in_mobile_app" />
      </LicenseNotification>
    )
  }
}

const LicenseInfo: React.FC = () => {
  const { t } = useLocalization()
  const { profile } = useSelector<any, { profile: DrivingProfile }>((state) => ({ profile: state.profile }))
  const driversLicense = profile.drivers_license ?? {}

  return (
    <LicenseInfoCard
      icon={<Icon.License />}
      title={t('dweb_account_driving_info_license_info')}
      info={
        profile.drivers_license
          ? t('dweb_account_driving_info_license_info_state_license_ending_exp_on', {
              state: driversLicense.state as string,
              lastFour: driversLicense.number_last_four as string,
              expirationDate: getLicenseExpirationDate(driversLicense),
            })
          : ''
      }
    >
      {() => (
        <FeatureFlag feature="drive_allow_license_updates">
          {(allowLicenseUpdatesEnabled) =>
            allowLicenseUpdatesEnabled ? (
              <LicenseInfoBody />
            ) : (
              <LicenseNotification>
                <LocalizedRichText
                  translationKey="dweb_account_driving_info_license_info_update_license_information_please_link1"
                  params={{
                    link1: (
                      <ExternalLink href={`${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/contact-us-dl-update`}>
                        <LocalizedText translationKey="dweb_account_driving_info_license_info_contact_us" />
                      </ExternalLink>
                    ),
                  }}
                />
              </LicenseNotification>
            )
          }
        </FeatureFlag>
      )}
    </LicenseInfoCard>
  )
}

export default LicenseInfo
